/**
 * Sticky in-page anchor navigation with horizontal scroll and active states.
 * Resolves template keys to Shopify section IDs (e.g. guide_questions →
 * #shopify-section-guide_questions or #shopify-section-template--…__guide_questions).
 */
class AnchorMenu extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #links = [];

  /** @type {Map<HTMLAnchorElement, HTMLElement>} */
  #targets = new Map();

  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#links = [...this.querySelectorAll('[data-anchor-link]')];
    this.#targets.clear();

    this.#links.forEach((link) => {
      const target = this.#resolveTarget(link);
      if (target) {
        this.#targets.set(link, target);
        link.dataset.resolvedTarget = target.id;
      }
      link.addEventListener('click', this.#onLinkClick);
    });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(this.#onIntersect, {
        rootMargin: '-35% 0px -55% 0px',
        threshold: 0,
      });

      this.#targets.forEach((target) => {
        this.#observer.observe(target);
      });
    }
  }

  disconnectedCallback() {
    this.#links.forEach((link) => {
      link.removeEventListener('click', this.#onLinkClick);
    });
    this.#observer?.disconnect();
  }

  /**
   * @param {HTMLAnchorElement} link
   * @returns {HTMLElement | null}
   */
  #resolveTarget(link) {
    const href = link.getAttribute('href') || '';
    const key = (link.dataset.sectionKey || '').trim();
    const candidates = [];

    if (href.startsWith('#')) {
      candidates.push(href.slice(1));
    }
    if (key) {
      candidates.push(`shopify-section-${key}`);
      candidates.push(key);
    }

    for (const id of candidates) {
      const exact = document.getElementById(id);
      if (exact) return exact;
    }

    if (key) {
      const bySuffix = document.querySelector(
        `[id^="shopify-section-"][id$="__${CSS.escape(key)}"], [id="shopify-section-${CSS.escape(key)}"]`
      );
      if (bySuffix instanceof HTMLElement) return bySuffix;

      const loose = [...document.querySelectorAll('[id^="shopify-section-"]')].find((el) => {
        const id = el.id || '';
        return id === `shopify-section-${key}` || id.endsWith(`__${key}`) || id.endsWith(`-${key}`);
      });
      if (loose instanceof HTMLElement) return loose;
    }

    return null;
  }

  /**
   * @param {Event} event
   */
  #onLinkClick = (event) => {
    const link = event.currentTarget;
    if (!(link instanceof HTMLAnchorElement)) return;

    const target = this.#targets.get(link) || this.#resolveTarget(link);
    if (!target) return;

    event.preventDefault();
    this.#targets.set(link, target);
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${target.id}`);
    this.#setActiveLink(link);
    link.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

    if (!visible?.target) return;

    const activeLink = [...this.#targets.entries()].find(([, target]) => target === visible.target)?.[0];
    if (activeLink) this.#setActiveLink(activeLink);
  };

  /**
   * @param {HTMLAnchorElement} activeLink
   */
  #setActiveLink(activeLink) {
    this.#links.forEach((link) => {
      const isActive = link === activeLink;
      link.classList.toggle('is-active', isActive);
      link.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('anchor-menu')) {
  customElements.define('anchor-menu', AnchorMenu);
}
